import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { FileReaderHelper, translateFileReadError } from '@/helpers/fileReaderHelper';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import alertIcon from '@/assets/icons/alert-circle.svg';
import addIcon from '@/assets/icons/add.svg';
import closeIcon from '@/assets/icons/close.svg';
import styles from './CompletionUploadSection.module.css';

interface CompletionUploadSectionProps {
    apiEndpoint: string;
    onSuccess: () => void;
    showAlert?: boolean;
    completionMessage?: string | null;
}

const CompletionUploadSection = ({ apiEndpoint, onSuccess, showAlert, completionMessage }: CompletionUploadSectionProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [fileComments, setFileComments] = useState<Record<number, string>>({});
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const { mutate: uploadDocuments } = usePostAuthApi<void, { documents: DocumentDtoOut[] }>(apiEndpoint);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = event.target.files;
        if (!fileList || fileList.length === 0) return;

        const newFiles = Array.from(fileList);
        const empty = newFiles.filter((f) => f.size === 0);
        const valid = newFiles.filter((f) => f.size > 0);
        if (empty.length > 0) {
            setUploadError(t('emptyFileSelected', { name: empty[0].name }));
        } else {
            setUploadError(null);
        }
        if (valid.length === 0) return;
        setSelectedFiles((prev) => [...prev, ...valid]);

        setTimeout(() => {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }, 0);
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setIsUploading(true);
        setUploadError(null);

        try {
            const documentPromises = selectedFiles.map(async (file, index) => {
                const docs = await FileReaderHelper.fileToDocumentDtoOut(file);
                return docs.map((doc) => ({
                    ...doc,
                    userComment: fileComments[index]?.trim() || undefined,
                }));
            });
            const documentsArrays = await Promise.all(documentPromises);
            const documents = documentsArrays.flat();

            uploadDocuments({ documents }, {
                onSuccess: () => {
                    setSelectedFiles([]);
                    setFileComments({});
                    setIsUploading(false);
                    onSuccess();
                },
                onError: () => {
                    setIsUploading(false);
                    setUploadError(t('documentUploadFailed'));
                },
            });
        } catch (error) {
            setIsUploading(false);
            setUploadError(translateFileReadError(error, t));
        }
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
        setFileComments((prev) => {
            const updated: Record<number, string> = {};
            Object.entries(prev).forEach(([key, value]) => {
                const k = Number(key);
                if (k < index) updated[k] = value;
                else if (k > index) updated[k - 1] = value;
            });
            return updated;
        });
    };

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                className={styles.hiddenInput}
            />

            {showAlert && (
                <div className={styles.completionSection}>
                    <div className={styles.completionHeader}>
                        <img src={alertIcon} alt="" className={styles.alertIcon} />
                        <div className="flex flex-col gap-1">
                            <span className="body-md-semibold text-(--font-heading)">{t('completionRequired')}</span>
                            <span className="body-sm-regular text-(--font-body-2)">{t('completionRequiredDescription')}</span>
                        </div>
                    </div>
                    {completionMessage && (
                        <div className={styles.completionMessage}>
                            <span className="body-sm-semibold">{t('messageFromOffice')}:</span>
                            <span className="body-sm-regular">{completionMessage}</span>
                        </div>
                    )}
                </div>
            )}

            <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
            >
                <img src={addIcon} alt="" />
                {t('addAdditionalDocument')}
            </button>

            {uploadError && (
                <span role="alert" className="body-sm-regular text-(--font-error)">
                    {uploadError}
                </span>
            )}

            {selectedFiles.length > 0 && (
                <div className={styles.uploadSection}>
                    <div className={styles.fileList}>
                        {selectedFiles.map((file, index) => (
                            <div key={`${file.name}-${index}`} className={styles.fileItem}>
                                <div className={styles.fileItemHeader}>
                                    <span className="body-sm-regular">{file.name}</span>
                                    <button onClick={() => removeFile(index)} className={styles.removeButton}>
                                        <img src={closeIcon} alt="" className={styles.removeIcon} />
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('addComment')}
                                    value={fileComments[index] || ''}
                                    onChange={(e) => setFileComments((prev) => ({ ...prev, [index]: e.target.value }))}
                                    className={styles.fileCommentInput}
                                />
                            </div>
                        ))}
                    </div>
                    <Button variant="primary" onClick={handleUpload} disabled={isUploading}>
                        {isUploading ? t('uploading') : t('saveDocuments')} ({selectedFiles.length})
                    </Button>
                </div>
            )}
        </>
    );
};

export default CompletionUploadSection;
